import {
    type App,
    computed,
    type ComputedRef,
    inject,
    readonly,
    type Ref,
    ref,
} from 'vue'
import type { Locale } from '@/i18n'
import { type ReactiveCookiePlugin } from '@/plugins/cookie-plugin'
import { type AppCurrency, currencies } from './currency-types'
import type { CountryPlugin } from '@/plugins/country.ts'

const currencyPluginKey = 'app.plugin.currency'
const useCurrencyKey = 'app.plugin.use-currency'

export { currencies }
export type { AppCurrency }
export const defaultCurrency: AppCurrency = 'GBP'
export type SelectCurrencyFunction = (currency: AppCurrency) => void
export type CurrencyPlugin = {
    currency: Readonly<Ref<AppCurrency | undefined>>
    currencies: Ref<AppCurrency[]>
    selectCurrency: SelectCurrencyFunction
}

export function isAppCurrency(
    currency: string | undefined
): currency is AppCurrency {
    return currencies.findIndex((curr) => curr === currency) !== -1
}

export function installCurrencyPlugin(
    app: App,
    options: {
        locale: Readonly<Ref<Locale>>
        cookies: ReactiveCookiePlugin
        countryPlugin: CountryPlugin
    }
): CurrencyPlugin {
    const { cookies } = options
    const { createCurrencyCookie } = cookies

    const currencyCookie = createCurrencyCookie()

    const currency: Ref<AppCurrency | undefined> = ref(currencyCookie.value)

    const selectCurrency: SelectCurrencyFunction = (c: AppCurrency): void => {
        // !!! Before you potentially make a mess of this:
        // We aren't exposing the currency cookie straight out of the cookie plugin on purpose
        // If cookies are disabled, then the select currency function might stop working altogether
        // So we're preserving a copy of the selected currency here and updating both the cookie and the local copy
        // The local copy is then exposed and used throughout the app
        currency.value = c
        currencyCookie.value = c
    }

    const currencies: Ref<AppCurrency[]> = ref([])

    const plugin: CurrencyPlugin = Object.freeze({
        currency: readonly(currency),
        currencies,
        selectCurrency,
    })

    app.provide(currencyPluginKey, plugin)

    const { countryPlugin } = options
    const { country } = countryPlugin
    const useCurrencyComputed = computed((): AppCurrency => {
        if (currency.value) {
            return currency.value
        }

        if (country.value?.currency) {
            return country.value.currency
        }

        return 'GBP'
    })
    app.provide(useCurrencyKey, useCurrencyComputed)

    return plugin
}

export function useCurrencyPlugin(): CurrencyPlugin {
    return inject(currencyPluginKey) as CurrencyPlugin
}

export function useCurrency(): ComputedRef<AppCurrency> {
    return inject(useCurrencyKey) as ComputedRef<AppCurrency>
}
